import {
    UserIcon,
    GraduationCapIcon,
    CodeIcon,
    BriefcaseIcon,
    FileTextIcon,
    CheckCircleIcon,
    AwardIcon,
    GraduationCap,
    Code,
    CheckSquare,
    Trophy,
} from "lucide-react";
import { Button } from "@component/button";
import { cn } from "@util/tailwind";

interface PreviewConfirmDetailsProps {
    onSubmit?: () => void;
}

const mockData = {
    name: "John Doe",
    email: "john.doe@email.com",
    phone: "+1 (555) 123-4567",
    location: "San Francisco, CA",
    position: "Senior Software Engineer",
    experience: "Experienced full-stack developer with 8+ years building scalable web applications using React, Node.js, and cloud technologies.",
    skills: ["React", "TypeScript", "Node.js", "Python", "AWS", "Docker", "PostgreSQL", "GraphQL"],
    education: [
        {
            degree: "B.S. Computer Science",
            institution: "Stanford University",
            period: "2012 - 2016",
            location: "Stanford, CA",
            description: "Graduated with honors. Focus on distributed systems and machine learning.",
        },
    ],
    workExperience: [
        {
            role: "Senior Software Engineer",
            institution: "Tech Corp Inc.",
            period_start_year: 2021,
            period_end_year: 9999,
            is_current: true,
            description: "Leading a team of 5 engineers building microservices architecture for the core platform.",
        },
        {
            role: "Software Engineer",
            institution: "StartupXYZ",
            period_start_year: 2018,
            period_end_year: 2021,
            is_current: false,
            description: "Built and maintained customer-facing React applications serving 100k+ users.",
        },
    ],
    projects: [
        {
            name: "E-Commerce Platform",
            role: "Lead Developer",
            year: "2023",
            description: "Full-stack e-commerce platform with real-time inventory management.",
            tech_stack: ["React", "Node.js", "MongoDB", "Redis"],
        },
    ],
    certifications: [
        {
            name: "AWS Solutions Architect",
            institution: "Amazon Web Services",
            date: "2023",
        },
    ],
    awards: [
        {
            name: "Best Innovation Award",
            institution: "Tech Corp Inc.",
            date: "2022",
            description: "Recognized for developing an AI-powered code review tool.",
        },
    ],
};

export default function PreviewConfirmDetails({ onSubmit }: PreviewConfirmDetailsProps) {
    return (
        <div className="w-full w-full max-w-2xl 2xl:max-w-3xl bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-4 md:p-8">
            <div className="flex items-center mb-1 justify-between">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                    Confirm Your Details
                </h2>
            </div>

            <p className="text-gray-600 dark:text-gray-400 text-sm md:text-md mb-9">
                We've extracted the following information from your resume.
                <br />Please review and make any necessary corrections.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Background */}
                <div className="md:col-span-2 mb-2 md:mb-4">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-1 md:gap-2 mb-3">
                        <UserIcon className="h-5 w-5 text-blue-500" />
                        Personal Background
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm text-black font-bold dark:text-gray-300 mb-1">Full Name</label>
                            <p className="text-gray-600 text-sm md:text-md dark:text-white">{mockData.name}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">Email</label>
                            <p className="text-gray-600 text-sm md:text-md dark:text-white">{mockData.email}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">Phone</label>
                            <p className="text-gray-600 text-sm md:text-md dark:text-white">{mockData.phone}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">Location</label>
                            <p className="text-gray-600 text-sm md:text-md dark:text-white">{mockData.location}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">Job Title</label>
                            <p className="text-gray-600 text-sm md:text-md dark:text-white">{mockData.position}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-black dark:text-gray-300 mb-1">Profile Summary</label>
                            <p className="text-gray-600 text-sm md:text-md dark:text-white">{mockData.experience}</p>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-2 md:my-4"></div>

                {/* Education */}
                <div className="md:col-span-2">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-1 md:gap-2 mb-3">
                        <GraduationCapIcon className="h-5 w-5 text-blue-500" />
                        Education
                    </h3>
                    {mockData.education.map((edu, index) => (
                        <div key={index} className={cn("relative pl-12", {
                            "pb-8": index < mockData.education.length - 1,
                        })}>
                            {index < mockData.education.length - 1 && (
                                <div className="absolute left-3 top-1 bottom-0 w-[2px]" style={{ backgroundColor: "#dcfce7" }}></div>
                            )}
                            <div className="absolute left-0 top-1">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center z-10" style={{ backgroundColor: "#dcfce7", border: "2px solid #22c55e" }}>
                                    <GraduationCap className="h-3 w-3" style={{ color: "#22c55e" }} />
                                </div>
                            </div>
                            <div>
                                <div className="mb-1 relative">
                                    <h5 className="font-medium text-gray-900 dark:text-gray-100 w-full text-left pr-24">{edu.degree}</h5>
                                    {edu.period && (
                                        <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">{edu.period}</span>
                                    )}
                                </div>
                                {(edu.institution || edu.location) && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                        {edu.institution}{edu.location && ` \u2022 ${edu.location}`}
                                    </p>
                                )}
                                {edu.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{edu.description}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Divider */}
                <div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-2 md:my-4"></div>

                {/* Skills */}
                <div className="md:col-span-2">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-1 md:gap-2 mb-3">
                        <CodeIcon className="h-5 w-5 text-blue-500" />
                        Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {mockData.skills.map((skill, index) => (
                            <span key={index} className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-3 py-1 rounded-full text-sm">
                                {skill}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Divider */}
                <div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-2 md:my-4"></div>

                {/* Work Experience */}
                <div className="md:col-span-2">
                    <h3 className="text-md md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                        <BriefcaseIcon className="h-5 w-5 text-blue-500" />
                        Work Experience
                    </h3>
                    {mockData.workExperience.map((job, index) => (
                        <div key={index} className={cn("relative pl-12", {
                            "pb-8": index < mockData.workExperience.length - 1,
                        })}>
                            {index < mockData.workExperience.length - 1 && (
                                <div className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30"></div>
                            )}
                            <div className="absolute left-0 top-1">
                                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400">
                                    <BriefcaseIcon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                </div>
                            </div>
                            <div>
                                <div className="mb-1 relative">
                                    <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left w-full pr-24">{job.role}</h5>
                                    <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                                        {job.period_start_year} - {job.is_current ? "Present" : job.period_end_year}
                                    </span>
                                </div>
                                {job.institution && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{job.institution}</p>
                                )}
                                {job.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{job.description}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Divider */}
                <div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-2 md:my-4"></div>

                {/* Projects */}
                <div className="md:col-span-2">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                        <FileTextIcon className="h-5 w-5 text-blue-500" />
                        Projects
                    </h3>
                    {mockData.projects.map((project, index) => (
                        <div key={index} className={cn("relative pl-12", {
                            "pb-8": index < mockData.projects.length - 1,
                        })}>
                            {index < mockData.projects.length - 1 && (
                                <div className="absolute left-3 top-1 bottom-0 w-[2px]" style={{ backgroundColor: "#f3e8ff" }}></div>
                            )}
                            <div className="absolute left-0 top-1">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center z-10 border-2" style={{ backgroundColor: "#f3e8ff", border: "1px solid #7c3aed" }}>
                                    <Code className="h-3 w-3" style={{ color: "#9333ea" }} />
                                </div>
                            </div>
                            <div>
                                <div className="flex sm:flex-row flex-col flex-wrap items-start gap-2 mb-1 relative">
                                    <h5 className="font-medium text-gray-900 dark:text-gray-100 w-full text-left">
                                        {project.name}{" "}
                                        {project.role && (
                                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full">
                                                {project.role}
                                            </span>
                                        )}
                                    </h5>
                                    {project.year && (
                                        <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">{project.year}</span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{project.description}</p>
                                {project.tech_stack.length > 0 && (
                                    <div className="mb-3 flex flex-row flex-wrap items-center gap-2">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Technologies:</p>
                                        {project.tech_stack.map((tech, techIndex) => (
                                            <span key={techIndex} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded-md">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Divider */}
                <div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-4"></div>

                {/* Certifications */}
                <div className="md:col-span-2">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                        <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                        Certifications
                    </h3>
                    {mockData.certifications.map((cert, index) => (
                        <div key={index} className={cn("relative pl-12", {
                            "pb-8": index < mockData.certifications.length - 1,
                        })}>
                            {index < mockData.certifications.length - 1 && (
                                <div className="absolute left-3 top-1 bottom-0 w-[2px]" style={{ backgroundColor: "#fce7f3" }}></div>
                            )}
                            <div className="absolute left-0 top-1">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center z-10 border-2" style={{ backgroundColor: "#fce7f3", border: "0.909091px solid #db2777" }}>
                                    <CheckSquare className="h-3 w-3" style={{ color: "#db2777" }} />
                                </div>
                            </div>
                            <div>
                                <div className="flex sm:flex-row flex-col flex-wrap items-start gap-2 mb-1 relative">
                                    <h5 className="font-medium text-gray-900 dark:text-gray-100 w-full text-left">{cert.name}</h5>
                                    {cert.date && (
                                        <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">{cert.date}</span>
                                    )}
                                </div>
                                {cert.institution && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{cert.institution}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Divider */}
                <div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 my-4"></div>

                {/* Awards */}
                <div className="md:col-span-2">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                        <AwardIcon className="h-5 w-5 text-blue-500" />
                        Awards
                    </h3>
                    {mockData.awards.map((award, index) => (
                        <div key={index} className={cn("relative pl-12", {
                            "pb-8": index < mockData.awards.length - 1,
                        })}>
                            {index < mockData.awards.length - 1 && (
                                <div className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30" style={{ backgroundColor: "#fef3c7" }}></div>
                            )}
                            <div className="absolute left-0 top-1">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center z-10" style={{ backgroundColor: "#fef3c7", border: "1.81818px solid #f59e0b" }}>
                                    <Trophy className="h-3 w-3" style={{ color: "#d97706" }} />
                                </div>
                            </div>
                            <div>
                                <div className="flex sm:flex-row flex-col flex-wrap items-start gap-2 mb-1 relative">
                                    <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left w-full">{award.name}</h5>
                                    {award.date && (
                                        <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">{award.date}</span>
                                    )}
                                </div>
                                {award.institution && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{award.institution}</p>
                                )}
                                {award.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{award.description}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-6 flex justify-end">
                <Button onClick={onSubmit} className="w-full md:w-auto">
                    Confirm and Continue
                </Button>
            </div>
        </div>
    );
}
